'use client';

import { m, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyFootNoteBold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import Image from 'next/image';
import {
  getOnboardingHost,
  type OnboardingComplianceBadge,
} from '../../../onboarding-host';
import {
  CalendarClockIcon,
  FileSparkleIcon,
  MicrophoneIcon,
  PenSparkleIcon,
  PhoneIcon,
  StarIcon,
  UserGroupIcon,
} from './assets/completion-step';

const COMPLIANCE_LOGO_SIZE = 28;

function BreatheWrap({
  children,
  className,
  delay = 0,
  enabled,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  enabled: boolean;
}) {
  if (!enabled) {
    // Still emit the wrapper when a caller sizes it, so a percentage-sized child
    // keeps a definite box to resolve against with animation switched off.
    return className ? <div className={className}>{children}</div> : <>{children}</>;
  }

  return (
    <m.div
      className={className}
      animate={{ scale: [1, 1.08, 1] }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay,
      }}
    >
      {children}
    </m.div>
  );
}

function IllustrationCard({ animate }: { animate: boolean }) {
  return (
    <div className="relative h-[336px] w-[548px]">
      <div className="absolute left-[212px] top-[112.5px] z-10 flex h-[144px] w-[131px] flex-col gap-2 rounded-2xl bg-surface p-4 shadow-lg">
        <div className="flex flex-col gap-2">
          <div className="h-2 w-[65px] rounded-full bg-text-tertiary" />
          <div className="h-2 w-[81px] rounded-full bg-black/15" />
          <div className="h-2 w-full rounded-full bg-black/15" />
          <div className="h-2 w-[93px] rounded-full bg-black/15" />
        </div>
        <div className="flex flex-col gap-2">
          <div className="h-2 w-[65px] rounded-full bg-text-tertiary" />
          <div className="h-2 w-[90px] rounded-full bg-black/15" />
          <div className="h-2 w-full rounded-full bg-black/15" />
        </div>
      </div>

      <BreatheWrap delay={0} enabled={animate}>
        <HeidiLogoMark
          artwork="standard"
          className="absolute left-[260px] top-[41px] size-9"
          data-testid="completion-preview-heidi-mark"
        />
      </BreatheWrap>
      <BreatheWrap delay={0.5} enabled={animate}>
        <StarIcon className="absolute left-[252px] top-[284px] size-[52px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={1.0} enabled={animate}>
        <MicrophoneIcon className="absolute left-[145px] top-[82.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={1.5} enabled={animate}>
        <CalendarClockIcon className="absolute left-[101px] top-[158.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={2.0} enabled={animate}>
        <PhoneIcon className="absolute left-[145px] top-[234.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={2.5} enabled={animate}>
        <PenSparkleIcon className="absolute left-[368px] top-[82.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={3.0} enabled={animate}>
        <FileSparkleIcon className="absolute left-[406px] top-[158.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
      <BreatheWrap delay={3.5} enabled={animate}>
        <UserGroupIcon className="absolute left-[368px] top-[234.5px] size-[44px] text-text-primary" />
      </BreatheWrap>
    </div>
  );
}

/**
 * Guided-tour Complete frame geometry, in Figma pixels. Everything below is
 * expressed as a percentage of the bounding box so the orbit scales down with
 * the preview pane instead of clipping on narrower laptops.
 *
 * Angles run clockwise from 12 o'clock and follow the country badge order —
 * for AU that is APP (bottom-right), ISO (top-right), SOC (left).
 */
const ORBIT_ANGLES_DEG = [128.3, 23, 258.6];
const ORBIT_RING_SIZE = 479;
const ORBIT_BADGE_TILE = 96;
const ORBIT_BADGE_TILE_RADIUS = 26.274;
const ORBIT_BADGE_ICON = 56;
const ORBIT_LOGO_SIZE = 120;
const ORBIT_BOX_SIZE = ORBIT_RING_SIZE + ORBIT_BADGE_TILE;

const asBoxPercent = (px: number) => `${(px / ORBIT_BOX_SIZE) * 100}%`;
const ORBIT_RADIUS_PERCENT = (ORBIT_RING_SIZE / 2 / ORBIT_BOX_SIZE) * 100;
const ORBIT_TILE_HALF_PERCENT = (ORBIT_BADGE_TILE / 2 / ORBIT_BOX_SIZE) * 100;
const ORBIT_TILE_CORNER_PERCENT = `${(ORBIT_BADGE_TILE_RADIUS / ORBIT_BADGE_TILE) * 100}%`;
const ORBIT_ICON_PERCENT = `${(ORBIT_BADGE_ICON / ORBIT_BADGE_TILE) * 100}%`;

function OrbitingComplianceRing({
  badges,
  animate,
}: {
  badges: OnboardingComplianceBadge[];
  animate: boolean;
}) {
  return (
    <div className="relative aspect-square w-full" style={{ maxWidth: ORBIT_BOX_SIZE }}>
      {/* The ring, tiles and logo are fixed brand artwork sitting on a photo, so
          they must not flip with the theme — hence primitives plus on-dark text. */}
      <div
        className="absolute rounded-full border border-sand-50"
        style={{
          width: asBoxPercent(ORBIT_RING_SIZE),
          height: asBoxPercent(ORBIT_RING_SIZE),
          left: asBoxPercent(ORBIT_BADGE_TILE / 2),
          top: asBoxPercent(ORBIT_BADGE_TILE / 2),
        }}
      />

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          style={{
            width: asBoxPercent(ORBIT_LOGO_SIZE),
            height: asBoxPercent(ORBIT_LOGO_SIZE),
          }}
        >
          <BreatheWrap enabled={animate} className="size-full">
            <HeidiLogoMark
              artwork="standard"
              className="size-full text-text-primary-on-dark"
            />
          </BreatheWrap>
        </div>
      </div>

      {badges.map((badge, index) => {
        const angle = (ORBIT_ANGLES_DEG[index] ?? 0) * (Math.PI / 180);
        return (
          <div
            key={badge.alt}
            data-testid="compliance-logo"
            className="absolute flex items-center justify-center bg-sand-50"
            style={{
              width: asBoxPercent(ORBIT_BADGE_TILE),
              height: asBoxPercent(ORBIT_BADGE_TILE),
              borderRadius: ORBIT_TILE_CORNER_PERCENT,
              left: `${50 + ORBIT_RADIUS_PERCENT * Math.sin(angle) - ORBIT_TILE_HALF_PERCENT}%`,
              top: `${50 - ORBIT_RADIUS_PERCENT * Math.cos(angle) - ORBIT_TILE_HALF_PERCENT}%`,
            }}
          >
            <Image
              src={badge.iconSrc}
              alt={badge.alt}
              width={ORBIT_BADGE_ICON}
              height={ORBIT_BADGE_ICON}
              style={{ width: ORBIT_ICON_PERCENT, height: ORBIT_ICON_PERCENT }}
            />
          </div>
        );
      })}
    </div>
  );
}

export function CompletionPreview({
  countryCode,
  isGuidedTour = false,
}: {
  countryCode?: string;
  isGuidedTour?: boolean;
}) {
  const prefersReducedMotion = useReducedMotion();
  const { t } = useTranslations();
  const complianceBadges = getOnboardingHost().getOnboardingComplianceBadges(countryCode);

  if (isGuidedTour) {
    return (
      <div className="flex size-full items-center justify-center overflow-hidden p-6">
        <OrbitingComplianceRing
          badges={complianceBadges.slice(0, ORBIT_ANGLES_DEG.length)}
          animate={!prefersReducedMotion}
        />
      </div>
    );
  }

  return (
    // Illustration stays centered in the upper space; the compliance badges sit
    // at the bottom of the panel. The preview shell owns clipping, so this pane
    // hides overflow instead of exposing an inner scrollbar.
    <div className="flex size-full flex-col overflow-hidden">
      <div className="flex w-full flex-1 items-center justify-center py-6">
        <IllustrationCard animate={!prefersReducedMotion} />
      </div>
      {complianceBadges.length > 0 && (
        <div className="flex w-full shrink-0 flex-wrap items-center justify-center gap-x-5 gap-y-2.5 px-6 pb-8">
          {complianceBadges.map((badge) => (
            <div
              key={badge.alt}
              className="flex items-center gap-1.5"
              data-testid="compliance-logo"
            >
              <div className="flex size-7 shrink-0 items-center justify-center">
                <Image
                  src={badge.iconSrc}
                  alt={badge.alt}
                  width={COMPLIANCE_LOGO_SIZE}
                  height={COMPLIANCE_LOGO_SIZE}
                />
              </div>
              <TypographyFootNoteBold className="text-text-secondary">
                {t(badge.text)}
              </TypographyFootNoteBold>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
